import {
  HttpRequestEntity,
  HttpRequestModelType,
} from 'shared/domain/httpRequest/httpRequestModel';
import { isAbortError } from 'serviceWorker/repository/httpRequest/abortRequest';
import { CONFLICT } from 'shared/contants';
import { getErrorCode } from 'shared/utils/errorHandling';
import {
  NON_RETRYABLE_LOCAL_DOCUMENT_UPLOAD_ERROR_CODES,
} from 'shared/utils/errorHandling/constants';
import {
  broadcastCreatedDocumentationError,
  broadcastUpdatedDocumentationError,
  broadcastDocumentationMovedError,
  broadcastDocumentationDeletedError,
  broadcastDocumentationChangedMeanwhile,
} from 'serviceWorker/broadcasts/documentations';
import { broadcastFailUploadedDocument } from 'serviceWorker/broadcasts/documents';
import { hasStatus, isNotFoundOrForbidden } from './helpers';
import { classifyDocumentationConflict, isChangedMeanwhileConflict } from 'shared/domain/documentation/documentationConflict';
import { documentationsSync } from 'serviceWorker/services/documentations';

/**
 * Removes the queued creation of a file (a document, a documentation) the
 * server refused for good: its parent is gone or not accessible, or a limit is
 * reached. Resolves whether it was removed; any other failure is tried again.
 */
export async function removeRefusedFileCreation(
  httpRequests: { remove: (id: number) => Promise<unknown> },
  request: HttpRequestEntity,
  error: unknown
): Promise<boolean> {
  if (!isNotFoundOrForbidden(error) && !hasStatus(error, CONFLICT)) {
    return false;
  }
  await httpRequests.remove(request._id);
  return true;
}

export async function handleNonRetryableDocumentUploadError(
  httpRequests: { remove: (id: number) => Promise<unknown> },
  request: HttpRequestEntity,
  error: unknown
): Promise<boolean> {
  const isDocumentUploadRequest =
    request.method === 'POST' &&
    (request.entityType === HttpRequestModelType.document ||
      request.entityType === HttpRequestModelType.drawing);

  if (!isDocumentUploadRequest) {
    return false;
  }

  const errorCode = getErrorCode(error);
  const isNonRetryableDocumentUploadErrorByCode = Boolean(
    errorCode &&
      NON_RETRYABLE_LOCAL_DOCUMENT_UPLOAD_ERROR_CODES.includes(
        errorCode as (typeof NON_RETRYABLE_LOCAL_DOCUMENT_UPLOAD_ERROR_CODES)[number]
      )
  );

  if (!isNonRetryableDocumentUploadErrorByCode) {
    return false;
  }

  await httpRequests.remove(request._id);
  if (
    request.data &&
    'localId' in request.data &&
    typeof request.data.localId === 'number'
  ) {
    // Mark upload as failed for progress UI without emitting generic error toast.
    broadcastFailUploadedDocument(request.data.localId);
  }
  return true;
}

/** The documentation changes of the queue; a creation is not among them (its refusal is handled above). */
const DOCUMENTATION_CHANGE_TYPES: readonly HttpRequestModelType[] = [
  HttpRequestModelType.documentation,
  HttpRequestModelType.documentationVersion,
  HttpRequestModelType.documentationMove,
  HttpRequestModelType.documentationDelete,
  HttpRequestModelType.documentationRestore,
];

function isOvertakenDocumentationChange(request: HttpRequestEntity, error: unknown): boolean {
  return (
    request.method !== 'POST' &&
    DOCUMENTATION_CHANGE_TYPES.includes(request.entityType) &&
    isChangedMeanwhileConflict(classifyDocumentationConflict(error))
  );
}

/**
 * Drops a queued documentation change someone else overtook (409, PT-7117):
 * sending it again would not save it either. The documentation is downloaded
 * again, so the user sees and can redo the change on what is there now.
 */
export async function removeOvertakenDocumentationChange(
  httpRequests: { remove: (id: number) => Promise<unknown> },
  request: HttpRequestEntity,
  error: unknown
): Promise<boolean> {
  if (!isOvertakenDocumentationChange(request, error)) {
    return false;
  }
  await httpRequests.remove(request._id);
  documentationsSync.sync();
  return true;
}

export function broadcastDocumentationError(
  request: HttpRequestEntity,
  error: any
): void {
  if (isOvertakenDocumentationChange(request, error)) {
    broadcastDocumentationChangedMeanwhile(error);
    return;
  }
  if (request.method === 'POST' && !isAbortError(error)) {
    broadcastCreatedDocumentationError(error);
  } else if (
    request.method === 'PUT' &&
    request.entityType !== HttpRequestModelType.documentationMove
  ) {
    broadcastUpdatedDocumentationError(error);
  } else if (
    request.method === 'PUT' &&
    request.entityType === HttpRequestModelType.documentationMove
  ) {
    broadcastDocumentationMovedError(error);
  } else if (request.method === 'DELETE') {
    broadcastDocumentationDeletedError(error);
  }
}
